import { Box, Button, Icon } from "@salestrak/ui";
import React from "react";

import { AccountMenu } from "#app/components/navigation/account-menu";
import { WorkspaceSelector } from "#app/components/navigation/workspace-selector";
import { useGlobalSearchActions } from "#app/components/global-search/global-search-context";
import { useCanInvite } from "#app/lib/hooks/use-can-invite";
import { useCloseSidebarIfMobile } from "#app/lib/mobile-sidebar-context";
import { useNavDialogActions } from "#app/lib/nav-dialogs-context";

/**
 * The nav-drawer header — the compact tier only. `AppShell` renders it as the
 * top row of the slide-in drawer, above the module strip and module menu, so
 * workspace switching, search and the account menu present with the
 * navigation instead of spending a pinned header row.
 *
 * The other tiers have no header at all: the module rail carries the
 * workspace mark at its head and the account avatar at its foot, and search
 * lives on the module-menu header (`GlobalSearchButton`). The shell passes
 * nothing for `siteHeader` there.
 */
export function SiteHeader() {
  const { openSearch } = useGlobalSearchActions();
  // Invite rides the drawer header on the compact tier: a door that is on
  // screen from every module, not folded into a module menu or the
  // workspace menu. Same grant-driven gate as the Members page.
  const canInvite = useCanInvite();
  const closeSidebar = useCloseSidebarIfMobile();
  const { openInviteMembers } = useNavDialogActions();
  const openInvite = () => {
    closeSidebar();
    openInviteMembers();
  };

  return (
    <Box
      align="center"
      as="header"
      direction="row"
      gap="sm"
      paddingX="sm"
      paddingY="sm"
      position="relative"
      shrink={false}
    >
      {/* Identity leads at its natural width on the soft secondary fill —
          squared, so it reads as a menu trigger rather than a field pill,
          and one rung above the ghost icon actions beside it. The spacer
          pins search and account to the right edge. */}
      <Box minWidth={0} shrink>
        <WorkspaceSelector soft />
      </Box>
      <Box fill="flex" />
      {/* The wider gap keeps the coarse-pointer hit areas from stacking. */}
      <Box align="center" direction="row" gap="sm" shrink={false}>
        {/* Icon-only, the same ghost rung as search beside it: the drawer
            header is a row of glyphs, and a labeled pill among them read as
            the odd one out. The confusing icon was the SHEET's send glyph,
            not this one. */}
        {canInvite ? (
          <Button
            aria-label="Invite Members"
            onClick={openInvite}
            size="icon-sm"
            variant="ghost"
          >
            <Icon name="user-plus" size="md" />
          </Button>
        ) : null}
        {/* The drawer's ONE search — the module-menu header hides its own
            glyph on compact so the same action is never offered twice. */}
        <Button
          aria-label="Search everything"
          onClick={() => openSearch("site_header")}
          size="icon-sm"
          variant="ghost"
        >
          <Icon name="search" size="md" />
        </Button>
        <AccountMenu />
      </Box>
    </Box>
  );
}
